import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  PhoneAndroid as PhoneIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const POLL_INTERVAL = 2000; // 2秒ごとにポーリング
const POLL_TIMEOUT = 120000; // 2分でタイムアウト

/**
 * Square Terminal API 決済コンポーネント
 * POS→Square Terminal端末に金額を送信し、端末上で決済を完了させる。
 * 二度打ち不要。
 */
const SquareTerminalPayment = ({
  amount,
  orderId,
  storeId,
  onSuccess,
  onError,
  onCancel,
}) => {
  const [status, setStatus] = useState('idle'); // idle | creating | waiting | completed | error
  const [checkoutId, setCheckoutId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const pollRef = useRef(null);
  const timeoutRef = useRef(null);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(value);
  };

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const startCheckout = async () => {
    setStatus('creating');
    setErrorMessage('');

    try {
      const response = await api.post(`/api/stores/${storeId}/terminal/checkout`, {
        amount,
        orderId,
        idempotencyKey: `${orderId}-${Date.now()}`,
      });

      const { checkoutId: newCheckoutId } = response.data;
      setCheckoutId(newCheckoutId);
      setStatus('waiting');

      // ポーリング開始
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await api.get(
            `/api/stores/${storeId}/terminal/checkout/${newCheckoutId}/status`
          );
          const { status: checkoutStatus, payment } = statusRes.data;

          if (checkoutStatus === 'COMPLETED') {
            cleanup();
            setStatus('completed');
            onSuccess({
              checkoutId: newCheckoutId,
              paymentId: payment?.id,
              cardBrand: payment?.cardBrand,
              last4: payment?.last4,
            });
          } else if (checkoutStatus === 'CANCELED' || checkoutStatus === 'CANCEL_REQUESTED') {
            cleanup();
            setStatus('error');
            setErrorMessage('端末で決済がキャンセルされました');
          }
        } catch (pollError) {
          console.error('[SquareTerminal] ポーリングエラー:', pollError);
        }
      }, POLL_INTERVAL);

      // タイムアウト
      timeoutRef.current = setTimeout(() => {
        cleanup();
        setStatus('error');
        setErrorMessage('決済がタイムアウトしました（2分）。端末の状態を確認してください。');
      }, POLL_TIMEOUT);

    } catch (error) {
      console.error('[SquareTerminal] チェックアウト作成エラー:', error);
      setStatus('error');
      const msg = error.response?.data?.message || error.message || 'Square端末への接続に失敗しました';
      setErrorMessage(msg);
      onError({ message: msg });
    }
  };

  const handleCancel = async () => {
    cleanup();
    // サーバー側でキャンセル要求
    if (checkoutId) {
      try {
        await api.post(`/api/stores/${storeId}/terminal/checkout/${checkoutId}/cancel`);
      } catch (e) {
        console.error('[SquareTerminal] キャンセルエラー:', e);
      }
    }
    setStatus('idle');
    onCancel();
  };

  // 初回自動開始
  useEffect(() => {
    startCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      {status === 'creating' && (
        <>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="body1">
            Square端末に接続中...
          </Typography>
        </>
      )}

      {status === 'waiting' && (
        <>
          <PhoneIcon sx={{ fontSize: 64, color: 'primary.main', mb: 1 }} />
          <Typography variant="h4" fontWeight={700} color="primary.main" gutterBottom>
            {formatCurrency(amount)}
          </Typography>
          <Box sx={{
            p: 2,
            bgcolor: 'info.light',
            borderRadius: 2,
            mb: 2,
          }}>
            <Typography variant="body1" color="info.dark" fontWeight={600}>
              Square端末に金額が表示されています
            </Typography>
            <Typography variant="body2" color="info.dark" sx={{ mt: 0.5 }}>
              お客様にカードをタッチしてもらってください
            </Typography>
          </Box>

          <CircularProgress size={24} sx={{ mb: 1 }} />
          <Typography variant="caption" color="text.secondary" display="block">
            決済完了を待っています...
          </Typography>

          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCancel}
            sx={{ mt: 2 }}
          >
            キャンセル
          </Button>
        </>
      )}

      {status === 'error' && (
        <>
          <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
            {errorMessage}
          </Alert>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => {
                setStatus('idle');
                startCheckout();
              }}
            >
              再試行
            </Button>
            <Button
              variant="outlined"
              onClick={onCancel}
            >
              キャンセル
            </Button>
          </Box>
        </>
      )}

      {status === 'completed' && (
        <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
          <CreditCardIcon sx={{ fontSize: 48, color: 'success.dark' }} />
          <Typography variant="h6" color="success.dark" fontWeight={700}>
            決済完了
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SquareTerminalPayment;
